import { db } from './firebase'
import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore'
import groupService from './groupService'

class OrganizationService {
  constructor() {
    this.cache = new Map()
  }

  /**
   * Normalize Firestore organization id from profile fields (`organizations` is often `[{ id, name, role }]`).
   * @param {string|{ id?: string, organizationId?: string, orgId?: string }|null|undefined} ref
   * @returns {string|null}
   */
  coerceOrganizationId(ref) {
    if (ref == null) return null
    if (typeof ref === 'string') {
      const t = ref.trim()
      return t.length ? t : null
    }
    if (typeof ref === 'object') {
      return this.coerceOrganizationId(ref.id || ref.organizationId || ref.orgId)
    }
    return null
  }

  followedOrgLabel(ref) {
    const id = this.coerceOrganizationId(ref)
    if (id) return id
    if (typeof ref === 'string') return ref
    if (ref && typeof ref === 'object' && typeof ref.name === 'string') return ref.name
    return ''
  }

  /**
   * Get the primary organization ID for a user with smart fallback logic
   * @param {Object} userProfile - User profile object
   * @param {string} preferredOrgName - Preferred organization name (e.g., "Crisp Technologies LLC")
   * @returns {string|null} Organization ID or null if none found
   */
  getPrimaryOrganizationId(userProfile, preferredOrgName = null) {
    if (!userProfile) {
      console.log('🔍 OrganizationService: No user profile provided')
      return null
    }

    console.log('🔍 OrganizationService: Getting primary org for user:', userProfile.email)
    console.log('🔍 OrganizationService: User profile org fields:', {
      organizationId: userProfile.organizationId,
      organizations: userProfile.organizations,
      followedOrganizations: userProfile.followedOrganizations
    })

    const directId = this.coerceOrganizationId(userProfile.organizationId)
    if (directId) {
      console.log('✅ OrganizationService: Using direct organizationId:', directId)
      return directId
    }

    if (userProfile.organizations && userProfile.organizations.length > 0) {
      const orgId = this.coerceOrganizationId(userProfile.organizations[0])
      if (orgId) {
        console.log('✅ OrganizationService: Using organizations array:', orgId)
        return orgId
      }
    }

    if (userProfile.followedOrganizations && userProfile.followedOrganizations.length > 0) {
      let selectedOrg = null

      if (preferredOrgName) {
        selectedOrg = userProfile.followedOrganizations.find(org =>
          this.followedOrgLabel(org).toLowerCase().includes(preferredOrgName.toLowerCase())
        )
        if (selectedOrg) {
          const id = this.coerceOrganizationId(selectedOrg)
          if (id) {
            console.log('✅ OrganizationService: Found preferred organization:', id)
            return id
          }
        }
      }

      const patterns = [
        { keywords: ['crisp', 'technologies'], name: 'Crisp Technologies LLC' },
        { keywords: ['chimeo'], name: 'Chimeo' },
        { keywords: ['velocity'], name: 'Velocity' }
      ]

      for (const pattern of patterns) {
        selectedOrg = userProfile.followedOrganizations.find(org =>
          pattern.keywords.some(keyword => this.followedOrgLabel(org).toLowerCase().includes(keyword))
        )
        if (selectedOrg) {
          const id = this.coerceOrganizationId(selectedOrg)
          if (id) {
            console.log(`✅ OrganizationService: Found ${pattern.name} organization:`, id)
            return id
          }
        }
      }

      selectedOrg = userProfile.followedOrganizations[0]
      const fallbackId = this.coerceOrganizationId(selectedOrg)
      if (fallbackId) {
        console.log('✅ OrganizationService: Using first followed organization:', fallbackId)
        return fallbackId
      }
      const label = typeof selectedOrg === 'string' ? selectedOrg.trim() : ''
      if (label) {
        console.log('✅ OrganizationService: Using first followed organization (string):', label)
        return label
      }
    }

    console.log('❌ OrganizationService: No organization found for user')
    return null
  }

  /**
   * Get all organizations a user has access to
   * @param {Object} userProfile - User profile object
   * @returns {Array} Array of organization IDs
   */
  getAllUserOrganizations(userProfile) {
    const orgs = new Set()

    const add = (ref) => {
      const id = this.coerceOrganizationId(ref)
      if (id) orgs.add(id)
    }

    if (userProfile?.organizationId) add(userProfile.organizationId)

    if (userProfile?.organizations) {
      userProfile.organizations.forEach(org => add(org))
    }

    if (userProfile?.followedOrganizations) {
      userProfile.followedOrganizations.forEach(org => add(org))
    }

    return Array.from(orgs)
  }

  /**
   * Verify that an organization exists in Firestore
   * @param {string} organizationId - Organization ID to verify
   * @returns {Promise<boolean>} True if organization exists
   */
  async verifyOrganizationExists(organizationId) {
    const id = this.coerceOrganizationId(organizationId)
    if (!id) return false

    try {
      const orgRef = doc(db, 'organizations', id)
      const orgDoc = await getDoc(orgRef)
      const exists = orgDoc.exists()
      console.log(`🔍 OrganizationService: Organization ${id} exists:`, exists)
      return exists
    } catch (error) {
      console.error('❌ OrganizationService: Error verifying organization:', error)
      return false
    }
  }

  /**
   * Get groups for a user with guaranteed organization detection
   * @param {Object} userProfile - User profile object
   * @param {string} userId - User ID for access control
   * @param {string} preferredOrgName - Preferred organization name
   * @returns {Promise<Array>} Array of groups
   */
  async getGroupsForUser(userProfile, userId, preferredOrgName = 'Crisp Technologies LLC') {
    try {
      console.log('🔍 OrganizationService: Getting groups for user:', userId)

      // Get primary organization
      const primaryOrgId = this.getPrimaryOrganizationId(userProfile, preferredOrgName)
      
      if (!primaryOrgId) {
        console.log('❌ OrganizationService: No organization found for user')
        return []
      }

      // Verify organization exists
      const orgExists = await this.verifyOrganizationExists(primaryOrgId)
      if (!orgExists) {
        console.log(`❌ OrganizationService: Organization ${primaryOrgId} does not exist`)
        
        // Try fallback organizations
        const allOrgs = this.getAllUserOrganizations(userProfile)
        for (const fallbackOrgId of allOrgs) {
          if (fallbackOrgId !== primaryOrgId) {
            const fallbackExists = await this.verifyOrganizationExists(fallbackOrgId)
            if (fallbackExists) {
              console.log(`✅ OrganizationService: Using fallback organization: ${fallbackOrgId}`)
              return await this.fetchGroupsFromOrganization(fallbackOrgId, userId, userProfile)
            }
          }
        }
        
        return []
      }

      return await this.fetchGroupsFromOrganization(primaryOrgId, userId, userProfile)
    } catch (error) {
      console.error('❌ OrganizationService: Error getting groups for user:', error)
      return []
    }
  }

  /**
   * Fetch groups from a specific organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID for access control
   * @param {Object} userProfile - User profile for admin checks
   * @returns {Promise<Array>} Array of groups
   */
  async fetchGroupsFromOrganization(organizationId, userId, userProfile) {
    try {
      console.log(`🔍 OrganizationService: Fetching groups from organization: ${organizationId}`)
      
      const groupsQuery = query(collection(db, 'organizations', organizationId, 'groups'))
      const groupsSnapshot = await getDocs(groupsQuery)
      
      let groups = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      console.log(`🔍 OrganizationService: Raw groups from ${organizationId}:`, groups.length)

      // Filter active groups
      const activeGroups = groups.filter(group => group.isActive !== false)
      console.log(`🔍 OrganizationService: Active groups:`, activeGroups.length)

      const activeWithFollowers = await groupService.attachFollowerCountsToGroups(organizationId, activeGroups)

      // Apply access control for non-admin users
      if (!userProfile?.isOrganizationAdmin && !userProfile?.isPlatformAdmin) {
        const accessibleGroups = activeWithFollowers.filter(group => 
          group.isPublic === true || group.isPrivate !== true
        )
        console.log(`🔍 OrganizationService: Accessible groups for user:`, accessibleGroups.length)
        return accessibleGroups
      }

      console.log(`✅ OrganizationService: Returning all groups for admin user:`, activeWithFollowers.length)
      return activeWithFollowers
    } catch (error) {
      console.error('❌ OrganizationService: Error fetching groups from organization:', error)
      return []
    }
  }

  /**
   * Get organization name from ID
   * @param {string} organizationId - Organization ID
   * @returns {string} Organization name or ID if name not found
   */
  getOrganizationName(organizationId) {
    if (!organizationId) return 'Unknown Organization'
    
    // Convert ID to readable name
    return organizationId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }
}

export default new OrganizationService()
